import React, { useEffect, useState } from 'react';
import {
  Box,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Link,
  Typography,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import CloseIcon from '@mui/icons-material/Close';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import GavelIcon from '@mui/icons-material/Gavel';
import GoogleIcon from '@mui/icons-material/Google';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
  lookupThreats,
  lookupTakedowns,
  lookupGsbHistory,
  lookupFnCandidates,
  lookupCtiScore,
  domainPivotLinks,
  Domain360Result,
  Domain360CtiResult,
  Domain360ThreatHit,
  Domain360TakedownHit,
  Domain360GsbHit,
  Domain360FnHit,
} from '../../services/domain360';
import { getDisplayThreatName } from '../../utils/domainUtils';

interface Domain360DialogProps {
  domain: string;
  open: boolean;
  onClose: () => void;
}

type Loadable<T> = { loading: boolean; result?: T };

const SectionRow = ({
  icon,
  title,
  testId,
  loading,
  status,
  summary,
  searchLink,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  testId: string;
  loading: boolean;
  status?: 'found' | 'none' | 'error';
  summary?: string;
  searchLink?: { label: string; to: string };
  children?: React.ReactNode;
}) => (
  <Box data-testid={testId} sx={{ py: 1.5 }}>
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      {icon}
      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
        {title}
      </Typography>
      {loading && <CircularProgress size={14} />}
      {!loading && status === 'found' && (
        <Chip size="small" color="warning" label={summary} />
      )}
      {!loading && status === 'none' && (
        <Typography variant="caption" color="text.secondary">
          None found
        </Typography>
      )}
      {!loading && status === 'error' && (
        <Typography variant="caption" color="error">
          {summary || 'Source unavailable'}
        </Typography>
      )}
      {searchLink && (
        <Link
          component={RouterLink}
          to={searchLink.to}
          variant="caption"
          sx={{ ml: 'auto', whiteSpace: 'nowrap' }}
        >
          {searchLink.label}
        </Link>
      )}
    </Box>
    {children && <Box sx={{ mt: 0.5, pl: 4 }}>{children}</Box>}
  </Box>
);

const Domain360Dialog = ({ domain, open, onClose }: Domain360DialogProps) => {
  const [threats, setThreats] = useState<
    Loadable<Domain360Result<Domain360ThreatHit>>
  >({ loading: true });
  const [takedowns, setTakedowns] = useState<
    Loadable<Domain360Result<Domain360TakedownHit>>
  >({ loading: true });
  const [gsb, setGsb] = useState<Loadable<Domain360Result<Domain360GsbHit>>>({
    loading: true,
  });
  const [fn, setFn] = useState<Loadable<Domain360Result<Domain360FnHit>>>({
    loading: true,
  });
  const [cti, setCti] = useState<Loadable<Domain360CtiResult>>({
    loading: true,
  });

  useEffect(() => {
    if (!open || !domain) return undefined;
    const abort = new AbortController();
    setThreats({ loading: true });
    setTakedowns({ loading: true });
    setGsb({ loading: true });
    setFn({ loading: true });
    setCti({ loading: true });

    lookupThreats(domain, abort.signal).then(result => {
      if (!abort.signal.aborted) setThreats({ loading: false, result });
    });
    lookupTakedowns(domain, abort.signal).then(result => {
      if (!abort.signal.aborted) setTakedowns({ loading: false, result });
    });
    lookupGsbHistory(domain).then(result => {
      if (!abort.signal.aborted) setGsb({ loading: false, result });
    });
    lookupFnCandidates(domain).then(result => {
      if (!abort.signal.aborted) setFn({ loading: false, result });
    });
    lookupCtiScore(domain).then(result => {
      if (!abort.signal.aborted) setCti({ loading: false, result });
    });

    return () => abort.abort();
  }, [open, domain]);

  const countSummary = (result?: Domain360Result<any>) =>
    result ? `${result.total} found` : undefined;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      data-testid="domain360-dialog"
    >
      <DialogTitle
        sx={{ display: 'flex', alignItems: 'center', gap: 1, pr: 6 }}
      >
        <TravelExploreIcon color="primary" />
        <Box>
          <Typography variant="h6" component="div" sx={{ lineHeight: 1.2 }}>
            Domain 360
          </Typography>
          <Typography
            variant="body2"
            color="text.secondary"
            sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}
          >
            {domain}
          </Typography>
        </Box>
        <IconButton
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
          data-testid="domain360-close"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <SectionRow
          icon={<GppMaybeIcon fontSize="small" color="action" />}
          title="Threats"
          testId="domain360-section-threats"
          loading={threats.loading}
          status={threats.result?.status}
          summary={
            threats.result?.status === 'error'
              ? threats.result?.error
              : threats.result?.truncated
              ? `${threats.result.total}+ found`
              : countSummary(threats.result)
          }
          searchLink={{
            label: 'Search in dashboard',
            to: `/dashboard?threat_search=${encodeURIComponent(domain)}`,
          }}
        >
          {threats.result?.items.map(hit => (
            <Typography
              key={`${hit.protected_asset_id}-${hit.threat_id}`}
              variant="body2"
            >
              <Link
                href={`/threats/${hit.protected_asset_id}/${hit.threat_id}/${hit.company_id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                {getDisplayThreatName(hit.threat_url)}
              </Link>{' '}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                {hit.status} · {hit.protected_asset_name}
              </Typography>
            </Typography>
          ))}
          {threats.result &&
            threats.result.total > threats.result.items.length && (
              <Link
                component={RouterLink}
                to={`/domain-lookup?domains=${encodeURIComponent(domain)}`}
                onClick={onClose}
                variant="caption"
                data-testid="domain360-threats-see-all"
              >
                See all {threats.result.total}
                {threats.result.truncated ? '+' : ''} in Domain lookup
              </Link>
            )}
        </SectionRow>
        <Divider />
        <SectionRow
          icon={<GavelIcon fontSize="small" color="action" />}
          title="Takedowns"
          testId="domain360-section-takedowns"
          loading={takedowns.loading}
          status={takedowns.result?.status}
          summary={
            takedowns.result?.status === 'error'
              ? takedowns.result?.error
              : countSummary(takedowns.result)
          }
          searchLink={{
            label: 'Search takedowns',
            to: `/takedown?takedown_search=${encodeURIComponent(domain)}`,
          }}
        >
          {takedowns.result?.items.map(hit => (
            <Typography key={hit.id} variant="body2">
              <Link
                component={RouterLink}
                to={`/takedown/${hit.id}`}
                onClick={onClose}
              >
                {hit.url || hit.id}
              </Link>{' '}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                {hit.status}
              </Typography>
            </Typography>
          ))}
        </SectionRow>
        <Divider />
        <SectionRow
          icon={<GoogleIcon fontSize="small" color="action" />}
          title="GSB submissions"
          testId="domain360-section-gsb"
          loading={gsb.loading}
          status={gsb.result?.status}
          summary={
            gsb.result?.status === 'error'
              ? gsb.result?.error
              : countSummary(gsb.result)
          }
          searchLink={{ label: 'Open GSB history', to: '/gsb' }}
        >
          {gsb.result?.items.map(hit => (
            <Typography key={hit.id ?? hit.url} variant="body2">
              {hit.url}{' '}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                {hit.status} · {hit.created_on}
              </Typography>
            </Typography>
          ))}
        </SectionRow>
        <Divider />
        <SectionRow
          icon={<TravelExploreIcon fontSize="small" color="action" />}
          title="FN candidates"
          testId="domain360-section-fn"
          loading={fn.loading}
          status={fn.result?.status}
          summary={
            fn.result?.status === 'error'
              ? fn.result?.error
              : countSummary(fn.result)
          }
          searchLink={{
            label: 'Open in FN Discovery',
            to: `/fn-discovery?searchDomain=${encodeURIComponent(domain)}`,
          }}
        >
          {fn.result?.items.map(hit => (
            <Typography key={hit.id} variant="body2">
              {hit.domain}{' '}
              <Typography
                component="span"
                variant="caption"
                color="text.secondary"
              >
                {hit.review_status} · {hit.source}
              </Typography>
            </Typography>
          ))}
        </SectionRow>
        <Divider />
        <SectionRow
          icon={<QueryStatsIcon fontSize="small" color="action" />}
          title="PreCrime score (CTI)"
          testId="domain360-section-cti"
          loading={cti.loading}
          status={cti.result?.status}
          summary={
            cti.result?.status === 'error'
              ? cti.result?.error
              : cti.result?.score !== undefined
              ? `${cti.result.score}`
              : undefined
          }
          searchLink={{
            label: 'Open CTI graph',
            to: `/cti-graph?domain=${encodeURIComponent(domain)}`,
          }}
        >
          {cti.result?.scoreDate && (
            <Typography variant="caption" color="text.secondary">
              Scored {cti.result.scoreDate}
            </Typography>
          )}
        </SectionRow>
        <Divider />
        <Box
          sx={{
            pt: 1.5,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            flexWrap: 'wrap',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 600, mr: 0.5 }}>
            External tools
          </Typography>
          {domainPivotLinks(domain).map(pivot => (
            <Chip
              key={pivot.label}
              size="small"
              variant="outlined"
              icon={<OpenInNewIcon />}
              label={pivot.label}
              component="a"
              href={pivot.href}
              target="_blank"
              rel="noopener noreferrer"
              clickable
              data-testid={`domain360-pivot-${pivot.label
                .replace(/\W+/g, '-')
                .toLowerCase()}`}
            />
          ))}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default Domain360Dialog;
